import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useState,
  type ReactNode,
} from "react";
import { useBlocker } from "react-router-dom";
const DraftContext = createContext<(id: string, dirty: boolean) => void>(
  () => {},
);

export function ContentDraftProvider({ children }: { children: ReactNode }) {
  const [drafts, setDrafts] = useState<Set<string>>(new Set());
  const register = useCallback(
    (id: string, dirty: boolean) =>
      setDrafts((previous) => {
        if (previous.has(id) === dirty) return previous;
        const next = new Set(previous);
        if (dirty) next.add(id);
        else next.delete(id);
        return next;
      }),
    [],
  );
  const dirty = drafts.size > 0;
  const blocker = useBlocker(dirty);
  useEffect(() => {
    if (blocker.state === "blocked") {
      if (window.confirm("Leave this page and discard your unsaved changes?"))
        blocker.proceed();
      else blocker.reset();
    }
  }, [blocker]);
  useEffect(() => {
    if (!dirty) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [dirty]);
  return createElement(DraftContext.Provider, { value: register }, children);
}
export function useContentDraftGuard(dirty: boolean) {
  const register = useContext(DraftContext);
  const id = useId();
  useEffect(() => {
    register(id, dirty);
    return () => register(id, false);
  }, [id, dirty, register]);
}
